import { Video } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface PageShellProps {
  children: ReactNode;
  /**
   * `center` (default) places one card in the middle of the viewport, which
   * is every error and loading state. `scroll` stacks content from the top
   * and lets it scroll, for the operator screen, which is taller than any
   * viewport and must not be clipped by the shell's own overflow.
   */
  layout?: 'center' | 'scroll';
}

/**
 * The frame every non-call page sits in: the same header as the landing page,
 * then the content centred in what is left of the viewport.
 *
 * Dark, like everything else. These pages were the light marketing surface
 * back when the landing page was, and they were the last white screens left
 * once it moved to the call stage: arriving at a 404 or a failed join meant
 * the product turned white for one screen and then went dark again. The bloom
 * behind the content is the same one the pre-join screen uses, so the whole
 * outside-a-call path reads as one place.
 */
export function PageShell({ children, layout = 'center' }: PageShellProps) {
  return (
    <div className="page-shell">
      <header className="flex h-14 shrink-0 items-center justify-center border-b border-white/10">
        <Link
          to="/"
          className="flex items-center gap-2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#101012]"
        >
          <span className="grid size-6 place-items-center rounded-[7px] bg-linear-to-b from-[#3b9cff] to-blue">
            <Video className="size-3.5 text-white" aria-hidden="true" />
          </span>
          <span className="text-[0.9375rem] font-semibold tracking-tight text-call-ink">
            LiveKit
          </span>
        </Link>
      </header>

      <main
        className={
          layout === 'scroll'
            ? 'relative z-10 min-h-0 flex-1 overflow-y-auto p-6'
            : 'relative z-10 grid min-h-0 flex-1 place-items-center p-6'
        }
      >
        {children}
      </main>
    </div>
  );
}
